import { useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";

import Actionbar from "src/components/common/actionbar/Authactionbar";
import { ImagePreviewModal } from "src/components/common/form/ImagePreviewModal";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useInitUserSearchFilter } from "src/hooks/useInitUserSearchFilter";
import useUiuxStore from "src/store/useUiuxStore";
import { useMouseContent } from "src/utils/mouse/mouseContent";

import { Content_KSK2140_R01 } from "./Content_KSK2140_R01";
import { DetailTable_KSK2140_R01 } from "./DetailTable_KSK2140_R01";
import {
	KSK2140_R01DetailDto,
	KSK2140_R01HeaderDto,
	KSK2140_R01InspectDto,
	KSK2140_R01LotDto,
} from "./Dto_KSK2140_R01";
import { Info_KSK2140_R01 } from "./Info_KSK2140_R01";
import { InspectTable_KSK2140_R01 } from "./InspectTable_KSK2140_R01";
import { LotTable_KSK2140_R01 } from "./LotTable_KSK2140_R01";
import { SearchSection_KSK2140_R01 } from "./SearchSection_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";
import { Table_KSK2140_R01 } from "./Table_KSK2140_R01";

/** Info 2줄(품목/일자/라벨 + 검사자/승인자) 고정 높이 */
const INFO_FIXED_HEIGHT = 100;

const INFO_DETAIL_RESIZER = {
	code: "KSK2140_R01",
	type: "height" as const,
	storageKey: "KSK2140_R01-info-detail-split-height",
	min: 25,
	max: 80,
	default: 55,
};

const DETAIL_SIDE_RESIZER = {
	code: "KSK2140_R01_side",
	type: "width" as const,
	storageKey: "KSK2140_R01-detail-side-split-width",
	min: 30,
	max: 80,
	default: 55,
};

const INSPECT_LOT_RESIZER = {
	code: "KSK2140_R01_inspect_lot",
	type: "width" as const,
	storageKey: "KSK2140_R01-inspect-lot-split-width",
	min: 35,
	max: 75,
	default: 60,
};

export const Container_KSK2140_R01 = () => {
	useFetchActionbarAuth("KSK2140_R01");
	const { setMessage } = useUiuxStore();
	const { KSK2140_R01Filter, setKSK2140_R01Filter, KSK2140_R01WorkStandard, workStandardModalVisible, setWorkStandardModalVisible, workStandardSheetNo } = useKSK2140_R01Store();
	useInitUserSearchFilter(setKSK2140_R01Filter, KSK2140_R01Filter);
	const gridRef = useRef<AgGridReact<KSK2140_R01HeaderDto> | null>(null);
	const detailGridRef = useRef<AgGridReact<KSK2140_R01DetailDto> | null>(null);
	const inspectGridRef = useRef<AgGridReact<KSK2140_R01InspectDto> | null>(
		null,
	);
	const lotGridRef = useRef<AgGridReact<KSK2140_R01LotDto> | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const sidePanelRef = useRef<HTMLDivElement>(null);
	const inspectLotPanelRef = useRef<HTMLDivElement>(null);
	const isInfoDetailResizing = useRef(false);
	const isSideResizing = useRef(false);
	const isInspectLotResizing = useRef(false);

	const {
		splitValue: infoDetailSplitValue,
		handleMouseDown: handleInfoDetailMouseDown,
	} = useMouseContent({
		resizer: INFO_DETAIL_RESIZER,
		isResizing: isInfoDetailResizing,
		containerRef,
	});

	const {
		splitValue: sideSplitValue,
		handleMouseDown: handleSideMouseDown,
	} = useMouseContent({
		resizer: DETAIL_SIDE_RESIZER,
		isResizing: isSideResizing,
		containerRef: sidePanelRef,
	});

	const {
		splitValue: inspectLotSplitValue,
		handleMouseDown: handleInspectLotMouseDown,
	} = useMouseContent({
		resizer: INSPECT_LOT_RESIZER,
		isResizing: isInspectLotResizing,
		containerRef: inspectLotPanelRef,
	});

	const {
		fetchData,
		// fetchDetailList,
		defaultKSK2140_R01InspectRow,
		handleDeleteClick,
		handleSaveClick,
		initData,
		handleWorkStandardClick,
	} = Content_KSK2140_R01({ gridRef, detailGridRef, setMessage });

	const inspectDefaultRow = useMemo(
		() => defaultKSK2140_R01InspectRow(),
		[defaultKSK2140_R01InspectRow],
	);

	return (
		<div className="content-container">
			<Actionbar
				onFetchClick={fetchData}
				onDeleteClick={handleDeleteClick}
				onSaveClick={handleSaveClick}
				onInitClick={initData}
			/>
			<ImagePreviewModal
				isOpen={workStandardModalVisible}
				title={`작업표준${workStandardSheetNo ?? ""} - ${KSK2140_R01WorkStandard?.workplaceNm || KSK2140_R01WorkStandard?.workplaceCd || "Q100"} / ${KSK2140_R01WorkStandard?.itemCd || ""}`}
				image={workStandardSheetNo === 1 ? KSK2140_R01WorkStandard?.standardSheet || "" : KSK2140_R01WorkStandard?.standardSheet2 || ""}
				onClose={() => setWorkStandardModalVisible(false)}
			/>
			<SearchSection_KSK2140_R01 />

			<div
				ref={containerRef}
				className="flex flex-1 min-h-0 flex-col p-[12px] gap-[4px]"
			>
				<div
					className="w-full min-h-0 flex flex-col"
					style={{ height: `${infoDetailSplitValue}%` }}
				>
					<Table_KSK2140_R01
						gridRef={gridRef}
						defaultKSK2140_R01InspectRow={inspectDefaultRow}
						// fetchDetailList={fetchDetailList}
					/>
				</div>
				<div
					className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300 shrink-0"
					onMouseDown={(event) =>
						handleInfoDetailMouseDown("UD", event.nativeEvent)
					}
					onDragStart={(event) => event.preventDefault()}
				/>
				<div
					className="w-full min-h-0 flex flex-col gap-[4px]"
					style={{ height: `${100 - infoDetailSplitValue}%` }}
				>
					<div
						className="w-full shrink-0 overflow-hidden"
						style={{ height: INFO_FIXED_HEIGHT }}
					>
						<Info_KSK2140_R01 />
					</div>
					<div
						ref={sidePanelRef}
						className="w-full min-h-0 flex flex-1 gap-[4px]"
					>
						<div
							className="h-full min-h-0 min-w-0 flex flex-col"
							style={{ width: `${sideSplitValue}%` }}
						>
							<DetailTable_KSK2140_R01 gridRef={detailGridRef} />
						</div>
						<div
							className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300 shrink-0"
							onMouseDown={(event) =>
								handleSideMouseDown("LR", event.nativeEvent)
							}
							onDragStart={(event) => event.preventDefault()}
						/>
						<div
							ref={inspectLotPanelRef}
							className="h-full min-h-0 min-w-0 flex flex-row gap-[4px]"
							style={{ width: `${100 - sideSplitValue}%` }}
						>
							<div
								className="h-full min-h-0 min-w-0 flex flex-col"
								style={{ width: `${inspectLotSplitValue}%` }}
							>
								<InspectTable_KSK2140_R01
									gridRef={inspectGridRef}
									defaultKSK2140_R01InspectRow={inspectDefaultRow}
									handleWorkStandardClick={handleWorkStandardClick}
								/>
							</div>
							<div
								className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300 shrink-0"
								onMouseDown={(event) =>
									handleInspectLotMouseDown("LR", event.nativeEvent)
								}
								onDragStart={(event) => event.preventDefault()}
							/>
							<div
								className="h-full min-h-0 min-w-0 flex flex-col"
								style={{ width: `${100 - inspectLotSplitValue}%` }}
							>
								<LotTable_KSK2140_R01
									lotGridRef={lotGridRef}
									onSaveClick={handleSaveClick}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
